import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import { api } from '../../../shared/services/API'
import { usePlatformAuth } from '../context/PlatformAuthContext'
import { PERM } from '../platformRoles'
import {
    ESTADO_VALIDACAO_LABEL,
    ESTADO_VALIDACAO_TONE,
    formatData,
    formatDataHora,
    type Jurisdicao,
    type VersaoResumo,
} from '../regrasJuridicasModel'

/** As versões de regras de uma jurisdição, da mais recente para a mais antiga. */
export default function RegrasJuridicasVersoes() {
    const { jurisdicaoId } = useParams<{ jurisdicaoId: string }>()
    const navigate = useNavigate()
    const { hasPerm } = usePlatformAuth()
    const podeEditar = hasPerm(PERM.REGRAS_JURIDICAS_MANAGE)

    const [jurisdicao, setJurisdicao] = useState<Jurisdicao | null>(null)
    const [versoes, setVersoes] = useState<VersaoResumo[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const carregar = useCallback(() => {
        if (!jurisdicaoId) return
        setIsLoading(true)
        setError('')
        Promise.all([
            api.get<Jurisdicao>(`/api/admin/regras-juridicas/jurisdicoes/${jurisdicaoId}`),
            api.get<VersaoResumo[]>(`/api/admin/regras-juridicas/jurisdicoes/${jurisdicaoId}/versoes`),
        ])
            .then(([j, v]) => {
                setJurisdicao(j.data)
                setVersoes([...v.data].sort((a, b) => b.numero - a.numero))
            })
            .catch(() => setError('Não foi possível carregar as versões desta jurisdição.'))
            .finally(() => setIsLoading(false))
    }, [jurisdicaoId])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- carregar alterna a sua própria flag de loading
        carregar()
    }, [carregar])

    return (
        <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/regras-juridicas')} sx={{ mb: 2, color: 'text.secondary', pl: 0 }}>
                Jurisdições
            </Button>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {jurisdicao ? `${jurisdicao.codigo} — ${jurisdicao.nome}` : 'Jurisdição'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {jurisdicao?.regimeJuridico || 'Versões das regras de assembleia'}
                    </Typography>
                </Box>
                {podeEditar && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/regras-juridicas/${jurisdicaoId}/versoes/nova`)}>
                        Nova versão
                    </Button>
                )}
            </Box>

            {jurisdicao && jurisdicao.versaoValidada === null && (
                <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                    Não há versão validada em vigor. Enquanto não houver, os condomínios desta jurisdição ficam sem regra
                    aplicável e os resultados das assembleias, inconclusivos.
                </Alert>
            )}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Versão</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Vigência</TableCell>
                                <TableCell>Fonte legal</TableCell>
                                <TableCell align="right">Lacunas</TableCell>
                                <TableCell>Criada por</TableCell>
                                <TableCell>Validada</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : versoes.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>Ainda não há versões de regras.</TableCell></TableRow>
                            ) : (
                                versoes.map((v) => (
                                    <TableRow key={v.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/regras-juridicas/versoes/${v.id}`)}>
                                        <TableCell sx={{ fontWeight: 600 }}>v{v.numero}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={ESTADO_VALIDACAO_LABEL[v.estado]} sx={{ fontWeight: 600, bgcolor: ESTADO_VALIDACAO_TONE[v.estado].bg, color: ESTADO_VALIDACAO_TONE[v.estado].color }} />
                                        </TableCell>
                                        <TableCell>{formatData(v.vigenciaInicio)} → {v.vigenciaFim ? formatData(v.vigenciaFim) : 'sem fim'}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', maxWidth: 280 }}>{v.fonteLegal || '—'}</TableCell>
                                        <TableCell align="right">
                                            {v.totalLacunas > 0
                                                ? <Chip size="small" label={v.totalLacunas} sx={{ fontWeight: 600, bgcolor: '#FEF3C7', color: '#92400E' }} />
                                                : <Typography variant="body2" color="text.secondary">0</Typography>}
                                        </TableCell>
                                        <TableCell>{v.criadaPorNome || '—'}</TableCell>
                                        <TableCell>{v.validadaPorNome ? `${v.validadaPorNome} · ${formatDataHora(v.validadaEm)}` : '—'}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    )
}
